import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  DatePicker,
} from 'antd';

import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import ModalDelete from 'components/ModalDelete';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import NumericInput from 'components/NumericInput';
import toCurrency from 'utils/toCurrency';
import {
  deleteCuotaFija, getCuotasFijas, patchCuotaFija, postCuotaFija,
} from 'api/catalogos/cuotaFijaParaVehiculos';
// import Select from 'components/Select';

// eslint-disable-next-line react/prop-types
const CuotaFijaParaVehiculos = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [sumTotal, setSumTotal] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    const _cuotas = await getCuotasFijas();
    setData(_cuotas);
    setLoading(false);
  };

  useEffect(() => {
    form.setFieldsValue({
      total: sumTotal,
    });
  }, [sumTotal]);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const _cuotas = await getCuotasFijas();

      if (mounted) {
        setData(_cuotas);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
    setVisibleAlert(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key ? patchCuotaFija(key, values)
        : postCuotaFija(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteCuotaFija(key);
    if (deleted) {
      onCancel();
      setVisibleAlert(false);
      await fetchData();
    }
    setLoading(false);
  };

  const onClickAdd = () => {
    setVisible(true);
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Desde',
      dataIndex: 'anio_inicial',
      key: 'anio_inicial',
      width: 100,
      render: (val) => (val?.format(DATE_FRONTEND_FORMAT)),
    },
    {
      titleText: 'Hasta',
      dataIndex: 'anio_final',
      key: 'anio_final',
      width: 100,
      render: (val) => (val?.format(DATE_FRONTEND_FORMAT)),
    },
    {
      titleText: 'Capacidad en KG',
      dataIndex: 'capacidad_inicial',
      key: 'capacidad_inicial',
      width: 300,
    },
    {
      titleText: 'Capacidad Máxima en KG',
      dataIndex: 'capacidad_final',
      key: 'capacidad_inicial',
      width: 300,
    },
    {
      titleText: 'De pasajeros',
      dataIndex: 'desde_pasajeros',
      key: 'desde_pasajeros',
      width: 300,
    },
    {
      titleText: 'Hasta pasajeros',
      dataIndex: 'hasta_pasajeros',
      key: 'hasta_pasajeros',
      width: 300,
    },
    {
      titleText: 'Impuesto',
      dataIndex: 'impuesto',
      key: 'impuesto',
      width: 150,
      render: (val) => toCurrency(val),
    },
    {
      titleText: 'Adicionales',
      dataIndex: 'adicionales',
      key: 'adicionales',
      width: 150,
      render: (val) => toCurrency(val),
    },
    {
      titleText: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 300,
      render: (val) => toCurrency(val),
    },
  ];
  const rules = {
    required: [
      {
        required: true,
        message: 'El campo es requerido',
      },
    ],
  };

  const focusInput = (currentInput) => {
    const input = document.getElementById(currentInput);
    if (input) {
      input.focus();
    }
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            mobileColIndex={0}
            rowKey="id"
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            allowImport
            permission={permission}
          />
        ) : (
          <Card
            bordered={false}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
              />
             )}
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Cuota Fija`}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              onValuesChange={(changedValues, allValues) => {
                const changed = Object.keys(changedValues).shift();
                const {
                  impuesto,
                  adicional,
                } = allValues;

                let totalValues = 0;
                if (impuesto) {
                  totalValues += (parseInt(impuesto, 10) || 0);
                }
                if (adicional) {
                  totalValues += (parseInt(adicional, 10) || 0);
                }
                setSumTotal(totalValues);
                setTimeout(() => {
                  focusInput(changed);
                });
              }}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="anio_inicial"
                    label="Año Inicial"
                  >
                    <DatePicker
                      format="YYYY"
                      picker="year"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="anio_final"
                    label="Año Final"
                    rules={rules.required}
                  >
                    <DatePicker
                      format="YYYY"
                      picker="year"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    name="capacidad_inicial"
                    label="Capacidad Inicial"
                    decimal
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    name="capacidad_final"
                    label="Capacidad Final"
                    decimal
                    required
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    name="desde_pasajeros"
                    label="Pasajeros Mínimos"
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    name="hasta_pasajeros"
                    label="Pasajeros Máximos"
                    required
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    name="impuesto"
                    label="Impuesto"
                    decimal
                    required
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    name="adicional"
                    label="Adicional"
                    decimal
                    required
                    currency
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    name="total"
                    label="Total"
                    decimal
                    required
                    disabled
                  />
                </Col>
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Row>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content="la Cuota Fija seleccionada?"
        />
      </Spin>
    </Row>
  );
};
export default CuotaFijaParaVehiculos;
